import {
  Button,
  FormikInput,
  theme
} from '@vfs-digital-channels/ns-react-components';
import {
  Field,
  Form,
  Formik
} from 'formik';
import React, {
  useEffect,
  useRef
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState
} from 'recoil';
import { ThemeProvider } from 'styled-components';
import * as yup from 'yup';
import { ApplicationRoutes, StaticRoutes } from '../../Router';
import {
  ApplyHeader,
  Footer,
  OtpEntryModal,
  SquircleCheckBox,
  SupportPopup
} from '../../components';
import TextLink from '../../components/Link/TextLink';
import { useModal } from '../../components/modalController/useModal';
import { Typography } from '../../components/typography/typography';
import {
  ApplicationSteps,
  CommonDetails,
  FormContainer,
  FormField,
  FormFieldWrapper,
  emailRegex,
  msisdnRegex,
  namesRegex
} from '../../constants';
import { ValidationErrorMessageMap } from '../../constants/validationErrorMessageMap';
import { findFirstValidationError } from '../../helpers/formik';
import {
  authDetail,
  continueClickedState,
  requestOtpRegister,
  requestRegisterOtpState,
  userDetails,
  verifyRegisterOtp,
  verifyRegisterOtpState
} from '../../recoil';
import { useTealium } from '../../tealium/useTealium';
import styles from './Registration.module.css';


const validationSchema = yup.object({
  firstName: yup.string().trim().matches(namesRegex, 'Name must contain letters only').min(1, 'Name should be of minimum 1 character length').max(50, 'Name should be of maximum 50 characters length').required('Name is a required field'),
  lastName: yup.string().trim().matches(namesRegex, 'Surname must contain letters only').min(1, 'Surname should be of minimum 1 character length').max(50, 'Surname should be of maximum 50 characters length').required('Surname is a required field'),
  email: yup.string().matches(emailRegex, 'Email address must be a valid email address').required('Email address is a required field'),
  msisdn: yup.string().matches(msisdnRegex, 'Mobile number must be a valid South African mobile number').required('Mobile number is a required field'),
  marketingConsent: yup.bool()
});

const wizardSteps = Object.values(ApplicationSteps).map((s) => ({
  label: s.title,
  subSteps: 0
}));

export const Registration = () => {

  const otpPending = useRef(false);
  const [otpResponse, setOtpRequest] = useRecoilState(requestOtpRegister);
  const [verifyOtpResponse, setOtpVerification] = useRecoilState(verifyRegisterOtp);
  const [getAuthDetail, setAuthDetail] = useRecoilState(authDetail);
  const userInfo = useRecoilValue(userDetails);
  const navigate = useNavigate();
  const location = useLocation();
  const [continueClicked, setContinueClicked] = useRecoilState(continueClickedState);
  const [openModal, closeModal] = useModal();
  const resetOtpRequestState = useResetRecoilState(requestRegisterOtpState);
  const resetOtpResponseState = useResetRecoilState(verifyRegisterOtpState);
  const resetLogin = useSetRecoilState(authDetail);
  const { track, events } = useTealium();

  let formTouched = false;

  const handleFormFocus = () => {
    if (formTouched) {
      return;
    }

    formTouched = true;
    track(events.registration.formStart());
  };

  useEffect(() => {
    track(events.registration.pageView());
  }, []);

  useEffect(() => {
    if (verifyOtpResponse?.token) {
      setAuthDetail(verifyOtpResponse);
    }
  }, [verifyOtpResponse, setAuthDetail]);

  useEffect(() => {
    if (getAuthDetail?.token) {
      // navigate is still not working
      // I just wasted 3 days trying to fix this. Defer investigation until later.
      // I suspect that the issue is that one of the atoms in login.js retains an error
      // state, even after logging in, so when the login is completed the console is
      // flooded with VlUnauthorized errors. Setting the window location as below
      // forces the browser to refresh, and all atoms are refreshed.
      window.location.pathname = ApplicationRoutes.personalDetails;
    }
  }, [getAuthDetail]);

  const sendOtpRequest = (userDetailsInput) => {

    if (!otpPending.current) {
      otpPending.current = true;
      setOtpRequest(userDetailsInput);
    }
  };

  useEffect(() => {
    if (otpResponse) {
      otpPending.current = false;
    }
  }, [otpResponse]);

  const DetailsEntry = () => {

    const formRef = useRef();

    const handleSubmit = () => {
      if (formRef.current) {
        track(events.registration.getStarted());
        formRef.current.handleSubmit();
      }
    };

    const submitDetails = (formValues) => {
      setContinueClicked(true);
      sendOtpRequest({
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        msisdn: formValues.msisdn,
        journeyType: 'register',
        marketingConsent: typeof formValues.marketingConsent === 'undefined' ? true: formValues.marketingConsent
      });
    };

    const goToPrivacyPolicyPage = () => {
      track(events.registration.TermConditionPrivacyPolicy('privacy policy'));
      window.open(CommonDetails.vodalendPrivacyPolicy, '_blank');
    };

    const goToTermsConditions = () => {
      track(events.registration.TermConditionPrivacyPolicy('terms and conditions'));
      window.open(CommonDetails.vodalendTermsConditions, '_blank');
    };

    const goToLogin = () => {
      track(events.registration.alreadyRegistered());
      navigate(StaticRoutes.login);
    };

    const handleFormikTouchedErrors = (touched, errors) => {
      const errorMessage = findFirstValidationError(errors, touched);

      if (!errorMessage) {
        return;
      }

      track(events.error('form', location.pathname, errorMessage));
    };

    return (
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          <Typography level={3} family={'medium'} align={'center'} className={styles.titleHeading}>
            Ready? Let&apos;s get you started
          </Typography>
          <Typography weight={'medium'} align={'center'} className={styles.titleSubHeading}>
            Please enter your personal details in the spaces provided below:
          </Typography>
          <Typography align={'center'} className={styles.titleSubHeading2}>
            Already registered?
            <span
              className={styles.boldLoginLink}
              onClick={goToLogin}
              onKeyDown={goToLogin}
              role='presentation'
            >
              &nbsp;Login
            </span>
          </Typography>
        </div>
        <ThemeProvider theme={theme.light}>
          <Formik
            initialValues={userInfo}
            onSubmit={submitDetails}
            validationSchema={validationSchema}
            validateOnMount={true}
            innerRef={formRef}
          >
            {({ values, errors, touched }) => {
              handleFormikTouchedErrors(touched, errors);

              return (
                <Form style={FormContainer} onFocus={handleFormFocus}>
                  <div style={FormFieldWrapper}>
                    <div style={FormField}>

                      <FormikInput label='Name' name='firstName' />
                    </div>
                    <div style={FormField}>

                      <FormikInput label='Surname' name='lastName' />
                    </div>
                    <div style={FormField}>

                      <FormikInput label='Email address' name='email' />
                    </div>
                    <div style={FormField}>

                      <FormikInput label='Mobile number' name='msisdn' />
                    </div>

                    <label style={ FormField } className={ styles.consent } htmlFor={ 'marketingConsent' }>
                      <Field
                        id={ 'marketingConsent' }
                        name={ 'marketingConsent' }
                        component={ SquircleCheckBox }
                        checked={values.marketingConsent}
                      />

                      <span>By checking this box you indicate that you would like to receive updates on our product offerings as well as other marketing communication which includes our monthly newsletter, tips and insights.</span>
                    </label>
                  </div>
                  <Footer rightCallback={Object.values(errors).length ? null : handleSubmit} rightTitle={'Get started'} />
                </Form>
              );
            }}
          </Formik>
        </ThemeProvider>
        <div className={styles.termsAndPolicy}>
          By continuing, I agree to the Vodacom
          <TextLink linkText=' Terms and conditions ' onClick={goToTermsConditions} />
          &
          <TextLink linkText='privacy policy' onClick={goToPrivacyPolicyPage} />
        </div>
        <SupportPopup />
      </div>
    );
  };

  const closeOtpModal = () => {
    closeModal();
    resetOtpResponseState();
    resetOtpRequestState();
    setContinueClicked(false);
  };

  const resetAllResponse = () => {
    resetOtpResponseState();
    resetOtpRequestState();
  };

  const submitOtp = (otp) => {
    track(events.otp.continue());
    setOtpVerification(otp);
  };

  const resendOtp = () => {
    track(events.otp.resend());
    setOtpRequest(null);
  };

  useEffect(() => {

    const errorMsg = ValidationErrorMessageMap[verifyOtpResponse?.message] ||
      ValidationErrorMessageMap[otpResponse?.message] || otpResponse?.error?.message || verifyOtpResponse?.error?.message;
    if (errorMsg) {

      if (errorMsg === ValidationErrorMessageMap.InvalidLoginRef) {
        track(events.error('form', location.pathname, errorMsg));
        resetLogin(null);
        openModal({
          title: 'Error',
          children: <div dangerouslySetInnerHTML={{ __html: 'Please Try again' }} />,
          type: 'error',
          onCloseModal: closeOtpModal,
          actions: [<Button key="close" onClick={closeOtpModal}>Close</Button>]
        });
        return;
      }

      if (errorMsg === 'The request was sent but no response was received') {
        track(events.error('form', location.pathname, errorMsg));

        openModal({
          title: 'Error',
          children: <div dangerouslySetInnerHTML={{ __html: ValidationErrorMessageMap[errorMsg] }} />,
          type: 'error',
          onCloseModal: closeOtpModal,
          actions: [<Button key="close" onClick={closeOtpModal}>Try again</Button>]
        });
        return;
      }

      track(events.error('event', location.pathname, errorMsg));
      openModal({
        title: 'Error',
        children: <div dangerouslySetInnerHTML={{ __html: errorMsg }} />,
        type: 'error',
        onCloseModal: closeOtpModal,
        actions: [<Button key="close" onClick={closeOtpModal}>Close</Button>]
      });
      return;
    }

    if (continueClicked) {
      openModal({
        children: <OtpEntryModal
          maskedContact={otpResponse?.maskedContact}
          verifyOtpResponse={verifyOtpResponse}
          otpResponse={otpResponse}
          submitOtp={submitOtp}
          resendOtp={resendOtp}
          resetAllResponse={resetAllResponse}
          onView={() => track(events.otp.pageView())}
        />,
        onCloseModal: closeOtpModal
      });
    }
  }, [otpResponse, continueClicked, verifyOtpResponse]);

  return (
    <div className={styles.loginPage}>
      <ApplyHeader
        currentStep={1}
        activeSubStep={0}
        steps={wizardSteps}
      />
      <DetailsEntry />
    </div>
  );
};
